import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAnalyzeStatus } from "@/lib/api";
import Header from "@/components/Header";

const STEPS = [
  "▸ Initializing secure analysis session",
  "▸ Verifying target accessibility",
  "▸ Conducting intelligence sweep",
  "▸ Mapping the monetization landscape",
  "▸ Benchmarking against industry patterns",
  "▸ Identifying revenue opportunities",
  "▸ Stress-testing each opportunity",
  "▸ Prioritizing by impact potential",
  "▸ Preparing executive summary",
  "▸ Finalizing your Blueprint",
];

export default function Analyzing() {
  const [params] = useSearchParams();
  const url = params.get("url");
  const job = params.get("job");
  const navigate = useNavigate();
  const [lines, setLines] = useState([]);
  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null); // {title} once the target is actually reached

  // Poll the background job every 2.5s — short requests survive mobile
  // browsers/networks that kill long-held connections (~60s limit on phones).
  useEffect(() => {
    if (!job) {
      navigate("/");
      return;
    }
    let polls = 0;
    const iv = setInterval(async () => {
      polls += 1;
      try {
        const s = await getAnalyzeStatus(job);
        if (s.site_verified_at) {
          setSiteInfo((prev) => prev || { title: s.site_title || "" });
        }
        if (s.status === "done" && s.report_id) {
          clearInterval(iv);
          navigate(`/report/${s.report_id}`);
        } else if (s.status === "error") {
          clearInterval(iv);
          setError(s.error || "Analysis failed");
        } else if (polls > 96) {
          // ~4 minutes — something is genuinely wrong
          clearInterval(iv);
          setError("This scan is taking unusually long. Please try again.");
        }
      } catch (e) {
        // Transient network blips while polling are fine — keep trying
        if (polls > 96) {
          clearInterval(iv);
          setError("Connection lost. Please try again.");
        }
      }
    }, 2500);
    return () => clearInterval(iv);
  }, [job, navigate]);

  // Animate terminal lines progress
  useEffect(() => {
    if (error) return;
    if (stepIdx >= STEPS.length) return;
    const t = setTimeout(() => {
      setLines((ls) => [...ls, { text: STEPS[stepIdx], status: "OK" }]);
      setStepIdx((i) => i + 1);
    }, 550 + Math.random() * 350);
    return () => clearTimeout(t);
  }, [stepIdx, error]);

  return (
    <div className="min-h-screen grain" data-testid="analyzing-page">
      <Header />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-16">
        <div className="font-mono-plex text-[11px] uppercase tracking-[0.3em] text-[#555] mb-3">
          ▸ Analyzing
        </div>
        <h1 className="font-serif-display text-4xl md:text-6xl font-black tracking-tighter leading-none break-words">
          {url}
        </h1>

        {/* Target verified — real proof-of-progress from the actual fetch */}
        {siteInfo && !error && (
          <div
            className="mt-8 border-2 border-[#0A8754] bg-white shadow-brutal p-4 md:p-5 animate-in fade-in slide-in-from-top-2 duration-500"
            data-testid="target-verified-card"
          >
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#0A8754] font-bold">
              ✓ Target verified
            </div>
            {siteInfo.title && (
              <div className="mt-1.5 font-serif-display text-xl md:text-2xl font-black leading-tight" data-testid="target-verified-title">
                {siteInfo.title}
              </div>
            )}
            <div className="mt-1 font-mono-plex text-[11px] uppercase tracking-widest text-[#555]">
              Site reached · Content secured · Analysis running
            </div>
          </div>
        )}

        <div className="mt-10 bg-[#0b0b0b] border-2 border-[#111] shadow-brutal-lg p-6 md:p-8 font-mono-plex text-sm text-[#9eff9e]" data-testid="terminal-output">
          <div className="flex items-center gap-2 text-[#9eff9e]/70 text-xs mb-4 uppercase tracking-widest">
            <span className="w-2 h-2 bg-[#FF331F]" />
            <span className="w-2 h-2 bg-[#F5A623]" />
            <span className="w-2 h-2 bg-[#0A8754]" />
            <span className="ml-3">monetizegps://analyze</span>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="flex justify-between gap-4 py-0.5">
              <span>{l.text}</span>
              <span className="text-[#0A8754]">[{l.status}]</span>
            </div>
          ))}
          {!error && stepIdx < STEPS.length && (
            <div className="py-0.5 terminal-cursor text-[#9eff9e]/80">
              {STEPS[stepIdx]}
            </div>
          )}
          {!error && stepIdx >= STEPS.length && (
            <div className="py-0.5 text-[#9eff9e]/80 terminal-cursor">▸ Final review in progress</div>
          )}
          {error && (
            <div className="mt-4 text-[#FF331F]" data-testid="analyze-error">
              [FATAL] {error}
              <button
                onClick={() => navigate("/")}
                className="block mt-4 underline text-[#9eff9e]"
                data-testid="back-home"
              >
                Return to scanner →
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
